import React, { useEffect, useState } from 'react';
import { base44 } from "@/api/base44Client";
import { useQuery } from '@tanstack/react-query';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { AlertTriangle, Loader2, Save, UserCircle, LogOut } from "lucide-react";
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';
import GoogleConnection from "@/components/GoogleConnection";
import RepProfileFields, { profileFrom, getMissingProfileFields } from '@/components/RepProfileFields';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import PageContainer from '@/components/PageContainer';

/**
 * Every rep's own page, not admin-gated: connecting Google is per-user OAuth
 * (worker/lib/google.ts), so Mike and Aaron need to reach this too, not just
 * Adam. This is also why, as of the self-service profile endpoint
 * (worker/routes/profile.ts), the rep's own editable fields live here rather
 * than only on the admin-only Admin screen: Mike and Aaron cannot reach
 * Admin, so this was the only page that could hold their own title, phone
 * numbers, booking link and vCard slug. One page, not a redirect to Admin,
 * a non-admin rep has no Admin screen to redirect to.
 */
export default function Profile() {
  const { user, isAdmin, checkUserAuth, logout } = useAuth();

  // Same query Home used to run. Cheap, and it keeps the spreadsheet id out
  // of the built bundle.
  const { data: sheet } = useQuery({
    queryKey: ['sheet-status'],
    queryFn: () => base44.sheets.status(),
    staleTime: 60_000,
  });

  // Task: split user Name into First/Last, 2026-09-20. New capability:
  // this page previously only ever displayed full_name as read-only text,
  // a rep had no way to correct their own name at all. Merged into the
  // same flat values object as everything else RepProfileFields owns
  // (unlike EditRep.jsx, which keeps name alongside email as a separate
  // concern from RepProfileFields' own value): this page has no separate
  // email field to mirror that split against, self-service email changes
  // are not offered at all, so there is no existing "identity fields live
  // outside RepProfileFields" convention here to match.
  const nameFrom = (u) => ({ first_name: u?.first_name || '', last_name: u?.last_name || '' });
  const [values, setValues] = useState(() => ({ ...nameFrom(user), ...profileFrom(user) }));
  const [saving, setSaving] = useState(false);
  const [missingFields, setMissingFields] = useState([]);

  // user arrives from AuthContext's own fetch, which can resolve after this
  // page has already mounted, so the form has to pick up the real values
  // once they land rather than being stuck on the empty defaults forever.
  useEffect(() => {
    setValues({ ...nameFrom(user), ...profileFrom(user) });
  }, [user]);

  // Not tied to a form submit event, so the top Save button (outside the
  // <form>) and the bottom one (a real submit button) can both call this the
  // same way (task: a Save button at the top too, 2026-09-15).
  const handleSave = async () => {
    const missing = [
      ...(values.first_name.trim() ? [] : [{ key: 'first_name', label: 'First name' }]),
      ...getMissingProfileFields(values),
    ];
    if (missing.length > 0) {
      setMissingFields(missing);
      return;
    }

    setSaving(true);
    try {
      // Blanks are sent as empty strings, which the API turns into NULL, so a
      // field can be cleared as well as set.
      await base44.entities.User.updateMyProfile(values);
      await checkUserAuth();
      toast.success('Profile saved');
    } catch (err) {
      toast.error(err.message || 'Could not save your profile');
    } finally {
      setSaving(false);
    }
  };

  // Task: simplify the device account's own profile page, 2026-09-16. A
  // shared booth login is not a person: it never captures or sends under
  // its own identity (the rep credited for a capture is whoever is chosen
  // on the capture screen, worker/routes/reps.ts), so none of the personal
  // rep fields below (title, phone, vCard, headshot, signature) mean
  // anything for it, and Google is never connected as this account either.
  // Login email is the one real thing this account has, and it is not
  // editable here (that stays an admin-only action on EditRep.jsx, same as
  // for any other account). Placed after every hook above so the hooks
  // themselves still run in the same order on every render regardless of
  // which account this is; only the returned JSX differs.
  if (user?.is_device_account) {
    return (
      <div className="min-h-screen bg-background safe-top pb-24">
        <PageContainer className="px-4 py-6">
          <div className="flex items-center gap-3 mb-6">
            <Link to="/"><Button variant="outline" className="min-h-9 px-4 text-sm">Back</Button></Link>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-foreground">Profile</h1>
              <p className="text-sm text-muted-foreground">{user?.full_name}</p>
            </div>
          </div>

          <Card className="p-4 bg-card">
            <div className="flex items-center gap-2 mb-3">
              <UserCircle className="w-4 h-4 text-accent shrink-0" />
              <h3 className="font-semibold text-foreground text-sm">Shared account</h3>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Login email</Label>
              <p className="text-sm font-medium text-foreground mt-1">{user?.email}</p>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              This is a shared device account, not a person. Contacts captured here are credited to
              whichever rep is chosen on the capture screen, not to this login, so there is no
              personal profile to fill in.
            </p>
          </Card>

          {/* Task: Sign Out on the Profile page, 2026-09-20, additive: the
              existing More menu option (BottomNav.jsx) is untouched. Makes
              sense here too, same as for a regular rep: whoever is
              physically holding a shared booth device still needs a way to
              end that session (handing the device to the next show, or
              locking it down at the end of a day) without hunting through
              the bottom nav's More sheet for it. Not wrapped in
              guardedAction: this page has no unsaved-changes guard of its
              own to begin with (there is nothing to edit here), matching
              the rest of this page rather than adding one just for this
              button. */}
          <Button
            variant="outline"
            onClick={() => logout()}
            className="w-full h-10 mt-4 text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
          >
            <LogOut className="w-4 h-4 mr-1.5" />
            Sign out
          </Button>
        </PageContainer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background safe-top pb-24">
      <PageContainer className="px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/"><Button variant="outline" className="min-h-9 px-4 text-sm">Back</Button></Link>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-foreground">Profile</h1>
            <p className="text-sm text-muted-foreground">{user?.full_name}</p>
          </div>
          {isAdmin && (
            <Link to="/admin">
              <Button variant="outline" size="sm" className="min-h-9 rounded-lg text-xs font-medium">Admin</Button>
            </Link>
          )}
        </div>

        {/* The "no calendar links" warning lives inside RepProfileFields' own
            Calendar Links section now, right next to the actual list. */}

        <Card className="p-4 bg-card mb-4">
          <div className="flex items-center gap-2 mb-3">
            <UserCircle className="w-4 h-4 text-accent shrink-0" />
            <h3 className="font-semibold text-foreground text-sm flex-1">Your profile</h3>
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="min-h-8 px-3 text-xs bg-accent hover:bg-accent/90 text-accent-foreground"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" />Save</>}
            </Button>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-3">
            {/* Task: split user Name into First/Last, 2026-09-20. New
                capability: previously read-only text (the subtitle under
                "Profile" above), a rep had no way to correct their own name
                at all. The server derives full_name from these two and
                keeps it in sync (worker/lib/userProfile.ts), so every
                existing display location that reads full_name (that same
                subtitle included) needed no change. */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">
                  First name<span className="text-destructive"> *</span>
                </Label>
                <Input
                  value={values.first_name}
                  onChange={e => setValues(p => ({ ...p, first_name: e.target.value }))}
                  disabled={saving}
                  className="mt-1 h-9 md:text-sm"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Last name</Label>
                <Input
                  value={values.last_name}
                  onChange={e => setValues(p => ({ ...p, last_name: e.target.value }))}
                  disabled={saving}
                  className="mt-1 h-9 md:text-sm"
                />
              </div>
            </div>
            {/* largeHeadshot, task: 2026-09-25: bigger specifically here,
                not on Edit Rep (EditRep.jsx passes no such prop, so it
                keeps the shared component's default size). */}
            <RepProfileFields value={values} onChange={setValues} disabled={saving} largeHeadshot />
            <Button type="submit" disabled={saving} className="w-full min-h-10 bg-accent hover:bg-accent/90 text-accent-foreground">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" />Save profile</>}
            </Button>
          </form>
        </Card>

        <div className="mb-4">
          <GoogleConnection />
        </div>

        {sheet?.sheet_url && (
          <a
            href={sheet.sheet_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 px-4 py-2.5 bg-card border border-border rounded-xl hover:bg-secondary/40 transition-colors mb-4"
          >
            <svg className="w-4 h-4 text-green-600 shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.5 3h-15A1.5 1.5 0 003 4.5v15A1.5 1.5 0 004.5 21h15a1.5 1.5 0 001.5-1.5v-15A1.5 1.5 0 0019.5 3zm-1.5 15H6V6h12v12z"/>
              <path d="M8 9h8v1.5H8zm0 3h8v1.5H8zm0 3h5v1.5H8z"/>
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground font-medium">Contacts Spreadsheet</p>
              <p className="text-sm font-semibold text-foreground">Open Google Sheet ↗</p>
            </div>
          </a>
        )}

        {/* Task: Sign Out on the Profile page, 2026-09-20, additive: the
            existing More menu option (BottomNav.jsx) is untouched, this is
            a second place to reach the same action. Not wrapped in
            guardedAction: unlike BottomNav's own Home/+/More navigation,
            this page has no unsaved-changes guard wired up for itself at
            all (a save-in-progress edit here already has no such
            protection today), so this matches the page's own existing
            behavior rather than adding guarding just for this one button. */}
        <Button
          variant="outline"
          onClick={() => logout()}
          className="w-full h-10 mb-4 text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
        >
          <LogOut className="w-4 h-4 mr-1.5" />
          Sign out
        </Button>

        <div className="h-8" />
      </PageContainer>

      {/* Named by label, not by field key, same pattern as Admin.jsx and
          Shows.jsx: a Save button that silently does nothing is worse than
          one that says exactly what it needs. */}
      <Dialog open={missingFields.length > 0} onOpenChange={(open) => { if (!open) setMissingFields([]); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Missing required fields
            </DialogTitle>
            <DialogDescription>
              Fill these in before saving:
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-1.5">
            {missingFields.map((field) => (
              <li key={field.key} className="flex items-center gap-2 text-sm text-foreground">
                <span className="w-1.5 h-1.5 rounded-full bg-destructive shrink-0" />
                {field.label}
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button onClick={() => setMissingFields([])} className="w-full">Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
